import LoadingSpinner from '@/components/LoadingSpinner';
import { currentCdnDirectoryAtom, userAtom } from '@/global-store';
import { BookmarkCategories } from '@/helpers/constants';
import { useFetchPdfDetails, useFetchPdfs } from '@http/queries';
import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IssueExportForm from '../IssueExportForm';
import { buildJiraAdfTable, headingAdf, subHeadingWithTextAdf } from './adfUtils';

export function PdfExport({
    todosToExport,
    jiraProject,
    jiraUsers,
    linkedJiraUserID,
    issueExportFormRef,
}) {
    const { t } = useTranslation();

    const [user] = useAtom(userAtom);
    const [cdnDirectory] = useAtom(currentCdnDirectoryAtom);
    const isUserPdf = todosToExport[0].category === BookmarkCategories.PDFS_UPLOAD;
    const pdfTitle = todosToExport[0].props.title;
    const pdfUrl = todosToExport[0].props.url;
    const uploadedBy = todosToExport[0].props.uploadedByUserEmail;
    const pdfScore = todosToExport[0].props.score;
    const improvePdf = false; //todosToExport[0].props.improvePdf;

    const { data: pdfsJson, isLoading: pdfsJsonLoading } = useFetchPdfs(isUserPdf);

    // const [pdfRules, setPdfRules] = useState([]);

    // const { data: _pdfRules, isLoading: pdfRulesLoading } = useFetchPdfErrorText();

    const { data: pdfDetails, isLoading: pdfDetailsLoading } = useFetchPdfDetails(
        pdfTitle,
        isUserPdf,
        improvePdf
    );

    const tableRows = useMemo(() => {
        if (pdfDetailsLoading || pdfsJsonLoading) return [];
        const summary = pdfDetails?.details?.ruleSummaries;
        const pdfsJsonRules = pdfsJson?.[pdfTitle]?.rules;
        if (!summary || !summary.length || !pdfsJsonRules) return [];

        const _tableRows = [];
        summary.forEach(_rule => {
            const { specification, clause, description, testNumber, status } = _rule;
            const occurrences = pdfsJsonRules[specification]?.[`${clause}.${testNumber}`];

            if (isNaN(occurrences)) return;

            const ruleNo = `${clause}.${testNumber}`;
            _tableRows.push([specification, ruleNo, description, occurrences]);
        });

        return _tableRows;
    }, [pdfDetails, pdfDetailsLoading, pdfsJsonLoading]);

    if (pdfDetailsLoading || pdfsJsonLoading) return <LoadingSpinner />;

    const headers = [
        t('pdfRuleSpecification'),
        t('ruleNumber'),
        t('description'),
        t('occurrences'),
    ];

    const jiraBodyDescripiton = [];

    // Title
    jiraBodyDescripiton.push(headingAdf(pdfTitle, 2));
    if (uploadedBy)
        jiraBodyDescripiton.push(subHeadingWithTextAdf(t('uploadedByUser'), uploadedBy));
    if (pdfUrl) jiraBodyDescripiton.push(subHeadingWithTextAdf(t('url'), pdfUrl, pdfUrl));

    jiraBodyDescripiton.push(subHeadingWithTextAdf(t('overallRating'), pdfScore + ' %'));

    // All failed rules on this pdf
    if (tableRows.length > 0) {
        const tableAdf = buildJiraAdfTable(headers, tableRows);
        if (JSON.stringify(tableAdf).length < 25000) {
            jiraBodyDescripiton.push(headingAdf(t('summaryOfFailedRulesOnPdf'), 3));
            jiraBodyDescripiton.push(tableAdf);
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <IssueExportForm
                ref={issueExportFormRef}
                todosToExport={todosToExport}
                jiraProject={jiraProject}
                jiraBodyDescripiton={jiraBodyDescripiton}
                jiraUsers={jiraUsers}
                linkedJiraUserID={linkedJiraUserID}
            />
        </Box>
    );
}
